/**
 * An outline made of straight lines only (2026-10-28, Adam: "THE BLUE SELECTION BORDERS MUST NEVER
 * DISAPPEAR WHEN ZOOMING OUT"). Far out, a selection ring is a line many times wider than what it is
 * round — a pinpoint's dot a pixel or two across. Safari draws a curve stroked wider than it is
 * tight as a band at the line's middle, a hole where the curve itself should be (a circle of radius 3
 * stroked 20 wide: paint from 7 to 13, nothing within): a dot's ring vanished whole, and a rounded
 * corner's would gape. Straight lines it draws whole, at any width (their joins round). So what a
 * ring is drawn round is first traced here — the same calls a canvas takes (`moveTo`, `lineTo`,
 * `arc`, `arcTo`, `roundRect`, `bezierCurveTo`), its curves cut into lines a fraction of a device
 * pixel from them — and the ring stroked along those.
 */

/** A run of the outline: its corners (x, y, …), and whether it closes on itself. */
export interface FlatRun {
  pts: number[];
  closed: boolean;
}

/** The calls a path is traced with (a canvas's own, the ones the outlines use). */
export interface PathSink {
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  closePath(): void;
  arc(x: number, y: number, r: number, a0: number, a1: number, ccw?: boolean): void;
  arcTo(x1: number, y1: number, x2: number, y2: number, r: number): void;
  roundRect(x: number, y: number, w: number, h: number, r?: number | number[]): void;
  bezierCurveTo(c1x: number, c1y: number, c2x: number, c2y: number, x: number, y: number): void;
  quadraticCurveTo(cx: number, cy: number, x: number, y: number): void;
  rect(x: number, y: number, w: number, h: number): void;
  ellipse(x: number, y: number, rx: number, ry: number, rot: number, a0: number, a1: number, ccw?: boolean): void;
}

/**
 * The outline `trace` draws, as straight runs: no point of a curve further than `tol` (its units)
 * from the lines that stand for it.
 */
export function flatten(trace: (sink: PathSink) => void, tol: number): FlatRun[] {
  const runs: FlatRun[] = [];
  let run: FlatRun | null = null;
  const t = Math.max(1e-6, tol);
  const at = (): [number, number] | null => (run && run.pts.length ? [run.pts[run.pts.length - 2]!, run.pts[run.pts.length - 1]!] : null);
  const start = (x: number, y: number) => {
    run = { pts: [x, y], closed: false };
    runs.push(run);
  };
  const to = (x: number, y: number) => {
    if (!run) return start(x, y);
    const p = at()!;
    if (p[0] !== x || p[1] !== y) run.pts.push(x, y);
  };
  /** Steps for an arc of radius `r` turning by `turn` radians, its chords within `t` of it. */
  const arcSteps = (r: number, turn: number) => {
    if (r <= t) return Math.max(1, Math.ceil(Math.abs(turn) / (Math.PI / 2)));
    const step = 2 * Math.acos(1 - t / r);
    return Math.max(1, Math.min(512, Math.ceil(Math.abs(turn) / step)));
  };
  const arcPts = (cx: number, cy: number, r: number, a0: number, turn: number) => {
    const n = arcSteps(r, turn);
    for (let i = 0; i <= n; i++) {
      const a = a0 + (turn * i) / n;
      to(cx + r * Math.cos(a), cy + r * Math.sin(a));
    }
  };
  const sink: PathSink = {
    beginPath() {
      runs.length = 0;
      run = null;
    },
    moveTo: start,
    lineTo: to,
    closePath() {
      if (!run) return;
      run.closed = true;
      // (a path goes on from where it closed: the start of the run)
      const [x, y] = [run.pts[0]!, run.pts[1]!];
      start(x, y);
    },
    arc(cx, cy, r, a0, a1, ccw = false) {
      let turn = a1 - a0;
      const full = Math.PI * 2;
      if (!ccw && turn < 0) turn = turn <= -full ? full : (turn % full) + full;
      else if (ccw && turn > 0) turn = turn >= full ? -full : (turn % full) - full;
      if (!ccw && turn > full) turn = full;
      if (ccw && turn < -full) turn = -full;
      // (a line from where the pen is to where the arc begins, as a canvas draws it)
      to(cx + r * Math.cos(a0), cy + r * Math.sin(a0));
      arcPts(cx, cy, Math.abs(r), a0, turn);
    },
    arcTo(x1, y1, x2, y2, r) {
      const p = at();
      if (!p) return start(x1, y1);
      const [x0, y0] = p;
      const ux = x0 - x1, uy = y0 - y1, vx = x2 - x1, vy = y2 - y1;
      const lu = Math.hypot(ux, uy), lv = Math.hypot(vx, vy);
      const cos = lu && lv ? (ux * vx + uy * vy) / (lu * lv) : 1;
      // in a line (or no turn to round): straight to the corner
      if (r <= 0 || !lu || !lv || Math.abs(cos) > 1 - 1e-9) return to(x1, y1);
      const half = Math.acos(Math.max(-1, Math.min(1, cos))) / 2;
      const d = r / Math.tan(half);
      const ax = x1 + (ux / lu) * d, ay = y1 + (uy / lu) * d;
      const bx = x1 + (vx / lv) * d, by = y1 + (vy / lv) * d;
      // the centre: along the corner's bisector, r / sin(half) from it
      const mx = ux / lu + vx / lv, my = uy / lu + vy / lv;
      const ml = Math.hypot(mx, my);
      const k = r / Math.sin(half) / ml;
      const cx = x1 + mx * k, cy = y1 + my * k;
      const a0 = Math.atan2(ay - cy, ax - cx);
      let turn = Math.atan2(by - cy, bx - cx) - a0;
      // (the short way round: a tangent arc never turns half a circle)
      if (turn > Math.PI) turn -= Math.PI * 2;
      if (turn < -Math.PI) turn += Math.PI * 2;
      to(ax, ay);
      arcPts(cx, cy, r, a0, turn);
    },
    roundRect(x, y, w, h, r = 0) {
      const rad = Math.max(0, Math.min(typeof r === 'number' ? r : (r[0] ?? 0), Math.abs(w) / 2, Math.abs(h) / 2));
      start(x + rad, y);
      to(x + w - rad, y);
      if (rad) arcPts(x + w - rad, y + rad, rad, -Math.PI / 2, Math.PI / 2);
      to(x + w, y + h - rad);
      if (rad) arcPts(x + w - rad, y + h - rad, rad, 0, Math.PI / 2);
      to(x + rad, y + h);
      if (rad) arcPts(x + rad, y + h - rad, rad, Math.PI / 2, Math.PI / 2);
      to(x, y + rad);
      if (rad) arcPts(x + rad, y + rad, rad, Math.PI, Math.PI / 2);
      sink.closePath();
    },
    bezierCurveTo(c1x, c1y, c2x, c2y, x, y) {
      const p = at() ?? [c1x, c1y];
      const [x0, y0] = p;
      // as many steps as keep the chords within t of the curve (by how sharply it bends: its second differences)
      const ddx = Math.max(Math.abs(x0 - 2 * c1x + c2x), Math.abs(c1x - 2 * c2x + x)),
        ddy = Math.max(Math.abs(y0 - 2 * c1y + c2y), Math.abs(c1y - 2 * c2y + y));
      const n = Math.max(1, Math.min(256, Math.ceil(Math.sqrt((6 * Math.hypot(ddx, ddy)) / (8 * t)))));
      for (let i = 1; i <= n; i++) {
        const s = i / n,
          q = 1 - s;
        to(q * q * q * x0 + 3 * q * q * s * c1x + 3 * q * s * s * c2x + s * s * s * x, q * q * q * y0 + 3 * q * q * s * c1y + 3 * q * s * s * c2y + s * s * s * y);
      }
    },
    quadraticCurveTo(cx, cy, x, y) {
      const [x0, y0] = at() ?? [cx, cy];
      sink.bezierCurveTo(x0 + (2 / 3) * (cx - x0), y0 + (2 / 3) * (cy - y0), x + (2 / 3) * (cx - x), y + (2 / 3) * (cy - y), x, y);
    },
    rect(x, y, w, h) {
      start(x, y);
      to(x + w, y);
      to(x + w, y + h);
      to(x, y + h);
      sink.closePath();
    },
    ellipse(cx, cy, rx, ry, rot, a0, a1, ccw = false) {
      let turn = a1 - a0;
      if (!ccw && turn < 0) turn += Math.PI * 2 * Math.ceil(-turn / (Math.PI * 2));
      if (ccw && turn > 0) turn -= Math.PI * 2 * Math.ceil(turn / (Math.PI * 2));
      turn = Math.max(-Math.PI * 2, Math.min(Math.PI * 2, turn));
      const n = arcSteps(Math.max(rx, ry), turn);
      const c = Math.cos(rot), sn = Math.sin(rot);
      for (let i = 0; i <= n; i++) {
        const a = a0 + (turn * i) / n;
        const ex = rx * Math.cos(a), ey = ry * Math.sin(a);
        to(cx + ex * c - ey * sn, cy + ex * sn + ey * c);
      }
    },
  };
  trace(sink);
  // (a run that is only where the pen was left, after a close: nothing)
  return runs.filter((r) => r.pts.length >= 4 || r.closed);
}

/** The straight runs as a path on `ctx` (a new one). */
export function flatPath(ctx: CanvasRenderingContext2D, runs: readonly FlatRun[]): void {
  ctx.beginPath();
  for (const r of runs) {
    ctx.moveTo(r.pts[0]!, r.pts[1]!);
    for (let i = 2; i < r.pts.length; i += 2) ctx.lineTo(r.pts[i]!, r.pts[i + 1]!);
    if (r.closed) ctx.closePath();
  }
}
